import { z } from 'zod';

import {
  zFulfillment,
  zOrderDetailPayload,
  zOrderResponse,
  zSlimPlanResponse,
} from '~/api/generated/zod.gen';
import type { OrderAction } from '~/constants/order-capabilities/types';
import {
  type ORDER_ABILITIES,
  ORDER_STATUSES,
  US_STATES,
  isClosedOrderStatus,
} from '~/utils/constants';
import { dateOnly } from '~/utils/datetime';
import {
  findMatchingVariant,
  isPropertyApplicable,
  unselectedVariantDimensionLabels,
} from '~/utils/filterPropertyOptions';

import type { ICDCode } from './icd';
import {
  type ApiProduct,
  NewProductVariantSchema,
  type Product,
  type ProductCategories,
  type ProductImages,
  type ProductProperties,
  ProductPropertiesSchema,
  type ProductVariant,
} from './product';

export const DEFAULT_LENGTH_OF_NEED = 99;

export type SavedProperty = {
  key: string;
  validationType: string | null;
  value: string;
};

const ApiOrderAddressSchema = z.object({
  city: z.string(),
  state: z.string(),
  street: z.string(),
  zipCode: z.string(),
  complement: z.string().nullish(),
});

const AuthorizationStatusSchema = zFulfillment.shape.authorization_status;
const MappyResultSchema = zFulfillment.shape.eligibility_result;
const PlanTypeSchema = zSlimPlanResponse.shape.plan_type;

const ApiFulfillmentSupplierSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  npi: z.string().optional(),
  taxId: z.string().nullish(),
  status: z.string().optional(),
  media: z.unknown().optional(),
  contact: z.unknown().optional(),
  address: z.unknown().optional(),
  zipCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  legacyId: z.int().nullish(),
  integrations: z.record(z.string(), z.unknown()).nullish(),
});

const ApiFulfillmentCarrierSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  isTemplate: z.boolean(),
  isActive: z.boolean(),
  isVisible: z.boolean(),
  statusUuid: z.string().nullable().optional(),
  dmeTemplateUuid: z.string().nullable().optional(),
  percentageModifier: z.number().nullable().optional(),
  frhcId: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  modifiedAt: z.string().nullable().optional(),
});

const ApiFulfillmentPlanSchema = z.object({
  uuid: z.string(),
  carrierCoverageZoneUuid: z.string(),
  planType: PlanTypeSchema,
  name: z.string(),
  isActive: z.boolean(),
  isVisible: z.boolean(),
  frhcId: z.string().nullable().optional(),
  candidPayerId: z.string().nullable().optional(),
  candidQuePayerId: z.string().nullable().optional(),
  authSource: z.string().nullable().optional(),
  paOption: zSlimPlanResponse.shape.pa_option,
  availitySubmittable: zSlimPlanResponse.shape.availity_submittable,
  createdAt: z.string().nullable().optional(),
  modifiedAt: z.string().nullable().optional(),
});

const ApiFulfillmentPlanCodeSchema = z.object({
  uuid: z.string(),
  planUuid: z.string(),
  code: z.string(),
  isActive: z.boolean(),
  isVisible: z.boolean(),
  createdAt: z.string().nullable().optional(),
  modifiedAt: z.string().nullable().optional(),
});

const ApiOrderFulfillmentSchema = z.object({
  id: z.string(),
  orderId: z.number(),
  hcpc: z.string(),
  isActive: z.boolean(),
  isAuthorized: z.boolean().nullable(),
  authorizationFingerprint: z.string().nullish(),
  authorizationDateEmbargo: z.string().nullable(),
  authorizationDateStart: z.string().nullish(),
  authorizationDateComplete: z.string().nullish(),
  authorizationLockExpiration: z.string().nullish(),
  authorizationExpiration: z.string().nullish(),
  authorizationStatus: AuthorizationStatusSchema.nullish(),
  authorizationSource: zFulfillment.shape.authorization_source,
  submittedBy: z.string().nullable().optional(),
  submittedVia: z.string().nullable().optional(),
  assignee: z
    .object({
      userId: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      /** SYSTEM = automation assignee; USER = human */
      type: z.enum(['SYSTEM', 'USER']).optional(),
    })
    .nullable()
    .optional(),
  submittedByActor: z
    .object({
      id: z.string().nullable().optional(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      /** SYSTEM = automation (SYSTEM_USER_ID); USER = human / legacy string submitter */
      type: z.enum(['SYSTEM', 'USER']).optional(),
    })
    .nullable()
    .optional(),
  jsonData: z
    .object({
      max_refills: z.number().optional(),
      productDetails: z
        .object({
          productId: z.string().nullish(),
        })
        .nullish(),
      availity: z
        .object({
          state: z.enum(['submitting', 'submitted', 'rejected', 'unknown']).optional(),
        })
        .passthrough()
        .nullish(),
    })
    .passthrough()
    .nullish(),
  eligibilityResult: MappyResultSchema.nullish(),
  eligibilityResultReason: z.string().nullish(),
  eligibilityContext: z.record(z.string(), z.unknown()).nullish(),
  billing: z
    .object({
      claimId: z.string().nullish(),
      externalId: z.string().nullish(),
      error: z.string().nullish(),
      claimUrl: z.string().nullish(),
      createdAt: z.string().nullish(),
      dateOfService: z.string().nullish(),
      installment: z.number().nullish(),
      status: z.string().nullish(),
    })
    .nullable()
    .array()
    .nullish(),
  supplier: ApiFulfillmentSupplierSchema.nullish(),
  carrier: ApiFulfillmentCarrierSchema.nullish(),
  plan: ApiFulfillmentPlanSchema.nullish(),
  planCode: ApiFulfillmentPlanCodeSchema.nullish(),
});

export type OrderFulfillment = ReturnType<typeof transformToOrder>['fulfillments'][number];

export type OrderItem = {
  categories: ProductCategories;
  description: string;
  hcpcCode: string[];
  icds: ICDCode[];
  properties: ProductProperties[];
  productId: string;
  quantity: number;
  refillAmount: number;
  title: string;
  images: ProductImages[];
  savedProperties: SavedProperty[];
  savedCheckoutProperties: SavedProperty[];
  variants: ProductVariant[];
};

export function toEmptyItem(product: ApiProduct): OrderItem {
  return {
    productId: product.productId,
    title: product.title,
    description: product.description,
    quantity: 0,
    refillAmount: 0,
    categories: {
      categoryName: product.categories.categoryName,
      ...(product.categories.parentCategory && {
        parentCategory: {
          categoryName: product.categories.parentCategory.categoryName,
        },
      }),
    },
    hcpcCode: [],
    icds: [],
    properties: product.properties.map((prop) => ({
      key: prop.key,
      type: prop.type,
      dependsOn: prop.dependsOn,
      displayText: prop.displayText ?? '',
      required: prop.required,
      validationType: prop.validationType,
      values: prop.values,
      category: '',
      subcategory: null,
    })),
    images: [],
    savedProperties: product.properties.map((prop) => ({
      key: prop.key,
      value: '',
      validationType: null,
    })),
    savedCheckoutProperties: [],
    variants: product.variants,
  };
}

export function groupByCategory(items: OrderItem[]): Map<string, OrderItem[]> {
  return items.reduce((acc, item) => {
    const category = item.categories.categoryName;
    if (!acc.has(category)) acc.set(category, []);
    acc.get(category)?.push(item);
    return acc;
  }, new Map<string, OrderItem[]>());
}

export function notSureSelected(item: OrderItem): boolean {
  if (item.properties.length === 0) return false;
  return !everyPropertySet(item);
}

const NOT_SURE_HCPC = 'T5999';

export function hasNotSureHcpc(item: OrderItem): boolean {
  return item.hcpcCode.map((hcpc) => hcpc.toUpperCase()).includes(NOT_SURE_HCPC);
}

export function getLengthOfNeed(item: OrderItem): number | undefined {
  // on every item, but instead of being a first class field like
  // quantity and refillAmount, it's a property :(
  const value = getCheckoutProperty(item, 'length_of_need');
  if (value === undefined) return undefined;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
}

export function setLengthOfNeed(item: OrderItem, lengthOfNeed: number): void {
  setCheckoutProperty(item, 'length_of_need', lengthOfNeed.toString());
}

function getCheckoutProperty(item: OrderItem, key: string): string | undefined {
  return item.savedCheckoutProperties.find((p) => p.key === key)?.value;
}

function setCheckoutProperty(item: OrderItem, key: string, value: string): void {
  const property = item.savedCheckoutProperties.find((p) => p.key === key);
  if (property) {
    property.value = value;
  } else {
    item.savedCheckoutProperties.push({
      key: key,
      validationType: '',
      value,
    });
  }
}

export function isNonEmpty(val: string | null | undefined): val is string {
  // for some reason graphql is returning empty strings when they would
  // more logically be null, so let's create a helper to categorize that issue
  return val != null && val !== '';
}

export function getAdditionalInfo(item: OrderItem): string | undefined {
  // this one is also on every item, but also a property like length of need :(
  return getCheckoutProperty(item, 'additional_information');
}

export function setAdditionalInfo(item: OrderItem, info: string): void {
  setCheckoutProperty(item, 'additional_information', info);
}

export function everyPropertySet(item: OrderItem): boolean {
  const selected = new Map(item.savedProperties.map((prop) => [prop.key, prop.value ?? '']));
  return item.properties.every((property) => {
    if (!isPropertyApplicable(property, selected, item.variants)) return true;
    return hasProperty(item, property.key);
  });
}

export function hasProperty(item: OrderItem, key: string): boolean {
  const propValue = getProperty(item, key);
  return isNonEmpty(propValue);
}

export function getProperty(item: OrderItem, key: string): string | undefined {
  return item.savedProperties.find((p) => p.key === key)?.value;
}

export function setProperty(item: OrderItem, key: string, value: string): void {
  // set property value, then unset any dependencies
  const property = item.savedProperties.find((p) => p.key === key);
  if (property) {
    property.value = value;
  } else {
    item.savedProperties.push({
      key: key,
      validationType: '',
      value,
    });
  }

  const dependencies = item.properties.filter((property) => property.dependsOn === key);
  dependencies.forEach((prop) => setProperty(item, prop.key, ''));
}

export function getVariantsHcpcs(item: OrderItem, product: Product): string[] | null {
  const itemVariant = findMatchingVariant(product.variants, item.savedProperties);
  return itemVariant ? itemVariant.attributes.hcpc : null;
}

function getSelectedVariant(item: OrderItem): ProductVariant | undefined {
  return findMatchingVariant(item.variants, item.savedProperties);
}

export type ItemUpdate = {
  product_id: string;
  variant_key: string;
  quantity: number;
  refill_amount: number;
  length_of_need: number;
  additional_information?: string;
  icds: { code: string; description: string }[];
};

function variantMatchError(item: OrderItem): Error {
  if (item.variants.length === 0) {
    return new Error(
      `Cannot generate documentation for "${item.title}": this product has no variants configured. Check the catalog setup.`
    );
  }

  const missing = unselectedVariantDimensionLabels(
    item.variants,
    item.savedProperties,
    item.properties
  );
  if (missing.length > 0) {
    return new Error(
      `Cannot generate documentation for "${item.title}": edit the item and select ${missing.join(', ')}, then try again.`
    );
  }

  return new Error(
    `Cannot generate documentation for "${item.title}": the selected options don't match a product variant. Edit the item and choose valid options.`
  );
}

export function formatForUpdate(item: OrderItem): ItemUpdate {
  const variant = getSelectedVariant(item);
  if (variant == null) throw variantMatchError(item);

  const additionalInfo = getAdditionalInfo(item);

  return {
    product_id: item.productId,
    variant_key: variant.key,
    quantity: item.quantity,
    refill_amount: item.refillAmount,
    length_of_need: getLengthOfNeed(item) ?? DEFAULT_LENGTH_OF_NEED,
    ...(additionalInfo && { additional_information: additionalInfo }),
    icds: item.icds.map((i) => ({ code: i.code, description: i.description })),
  };
}

export enum InsuranceEligibilityStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
  UNCHECKED = 'UNCHECKED',
  NEEDS_VERIFICATION = 'NEEDS_VERIFICATION',
  UNABLE_TO_VERIFY = 'UNABLE_TO_VERIFY',
}

export type InsuranceOrderCarrierType = z.infer<typeof InsuranceOrderCarrierTypeEnumSchema>;

export const InsuranceOrderCarrierTypeEnumSchema = z.enum([
  'medicare',
  'medicare_advantage',
  'medicaid',
  'medicaid_managed_care',
]);

export const AdditionalCoverageSchema = z.object({
  carrierType: z.string().nullish(),
  eligibilityInformation: z.string().nullish(),
  planName: z.string().nullish(),
  planCode: z.string().nullish(),
  planDescription: z.string().nullish(),
  certificateNumber: z.string().nullish(),
  entityIdentifier: z.string().nullish(),
  insuranceType: z.string().nullish(),
  typeEb03: z.string().nullish(),
  typeNm101: z.string().nullish(),
  effectiveDate: z.string().nullish(),
  terminationDate: z.string().nullish(),
  serviceTypes: z.array(z.string()).nullish(),
  message: z.string().nullish(),
  memberId: z.string().nullish(),
});

export type AdditionalCoverage = z.infer<typeof AdditionalCoverageSchema>;

export const CareOsPlanSchema = z.object({
  /** CareOS Plan.uuid denormalized onto Mongo; missing plan ⇒ Needs Review. */
  uuid: z.string(),
  /** CareOS Plan.name when linked; FE shows Plan not found when plan is absent. */
  name: z.string().nullish(),
  planType: PlanTypeSchema.nullish(),
  /** Active CareOS PlanCode.code values (catalog; not discovered insurance.planCode). */
  codes: z.array(z.string()).default([]),
});

export type CareOsPlan = z.infer<typeof CareOsPlanSchema>;

export const InsuranceOrderResponseSchema = z.object({
  memberId: z.string().nullable(),
  groupNumber: z.string().nullish(),
  name: z.string(),
  planCode: z.string().nullish(),
  /** CareOS Plan linkage nested on GET; null when unlinked. */
  plan: CareOsPlanSchema.nullish(),
  state: z.string(),
  carrierType: InsuranceOrderCarrierTypeEnumSchema.nullish(),
  carrierPriority: z.number().nullish(),
  effectiveDate: z.string().nullish(),
  terminationDate: z.string().nullish(),
  createdAt: z.string().nullish(),
  modifiedAt: z.string().nullish(),
  traceId: z.number().nullish(),
  frhcId: z.number().nullish(),
  /** Equivalent to frhcId for identifying the carrier; either alone can route an assignment. */
  stediId: z.string().nullish(),
  /** Medicare Part A/B entitlement the payer reported; null on non-Medicare lines. */
  medicareCoverage: z.string().nullish(),
  /** Provider whose call produced this line; null when hand-entered. */
  source: z.string().nullish(),
  /** frontrunner_audit_trail row recording the call that produced this line. */
  auditReferenceId: z.string().nullish(),
  /** Referral included this insurance at intake; discovery may have enriched it */
  referralSubmitted: z.boolean().nullish(),
  /** @deprecated Legacy API field; prefer referralSubmitted */
  patientSubmitted: z.boolean().nullish(),
  /** Ops added via UI; not from discovery */
  manuallyEntered: z.boolean().nullish(),
  /** Ops manually attested this insurance as verified. */
  manuallyVerifiedAt: z.string().nullish(),
  manuallyVerifiedBy: z.string().nullish(),
  manuallyVerifiedSource: z.string().nullish(),
  /** S3 URI for an uploaded insurance-card front image. */
  cardFront: z.string().nullish(),
  /** S3 URI for an uploaded insurance-card back image. */
  cardBack: z.string().nullish(),
  eligibility: z
    .object({
      status: z.enum(InsuranceEligibilityStatus).nullish(),
      reason: z.string().nullish(),
      checkedOn: z.string().nullish(),
      /** Provider whose call set this verdict; may differ from the line's own source. */
      source: z.string().nullish(),
      /** frontrunner_audit_trail row recording the call that set this verdict. */
      auditReferenceId: z.string().nullish(),
    })
    .nullish(),
  additionalCoverage: z.array(AdditionalCoverageSchema).nullish(),
});

export type InsuranceOrderResponse = z.infer<typeof InsuranceOrderResponseSchema>;

const OrderPatientSchema = z.object({
  patientId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  dob: z.string(),
  gender: z.string(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
});

const ApiOrderContactSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullable(),
  phoneType: z.string().nullable(),
  email: z.string().nullable(),
  preferredLanguage: z.string().nullable(),
  relationship: z.string().nullable(),
});

const ApiOrderPhysicianSchema = z.object({
  address: ApiOrderAddressSchema,
  email: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  npi: z.string(),
  phone: z.string().nullable(),
  patientLastVisit: z.string().nullish(),
});

export type OrderAssignee = {
  firstName: string;
  lastName: string;
  userId: string;
};

export type HistoryItem = {
  status: string; // Was ORDER_STATUSES, changed for backward compatability
  reason: string;
  description: string;
  createdAt: string;
};

export enum TelehealthOptions {
  ORDINARY = 'ORDINARY',
  TELEHEALTH = 'TELEHEALTH',
}

/** Known values for PrescriptionMetadata.source as set by the API. */
export enum PrescriptionSource {
  Docusign = 'Docusign',
  Manual = 'Manual',
  Backfill = 'Backfill',
}

export type PrescriptionMetadata = {
  signingDate: string | null;
  expirationDate: string | null;
  source: string | null;
  reference: string | null;
};

const ApiOrderItemSchema = z.object({
  productId: z.string(),
  title: z.string().nullable(), // TODO Shouldn't be nullable
  description: z.string().nullable(), // TODO Shouldn't be nullable
  quantity: z.number(),
  refillAmount: z.number(),
  hcpcCode: z.array(z.string().toUpperCase()),
  icds: z.array(
    z.object({
      code: z.string(),
      description: z.string().nullable(),
    })
  ),
  selectedProperties: z.array(
    z.object({
      key: z.string(),
      value: z.string().nullable(),
    })
  ),
  selectedCheckoutProperties: z.array(
    z.object({
      key: z.string(),
      value: z.string().nullable(),
    })
  ),
  properties: z.array(ProductPropertiesSchema),
  variants: NewProductVariantSchema.nullish(),
  images: z.array(
    z.object({
      square: z.string().nullable().optional(),
      thumbnail: z.string().nullable().optional(),
    })
  ),
  categories: z.array(
    z.string()
    // TODO Should be array of category shape below, no parent denominations
    //  array of string is even worse :|
    // z.object({
    //   uuid: z.string().optional(), // TODO Shouldn't be optional
    //   categoryName: z.string()
    // })
  ),
});

// Mirrors ConfirmationStatus in the API (application/models/orders/constants.py).
export const CONFIRMATION_STATUSES = [
  'NotApplicable',
  'Requested',
  'Confirmed',
  'Changed',
  'Skipped',
] as const;

export const ApiOrderShipmentSchema = z.object({
  trackingNumber: z.string().nullish(),
  shippingCarrierCode: z.string().nullish(),
  shippingCarrierName: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  expectedDeliveryAt: z.string().nullish(),
  // Status values are additive, so the badge label map decides what renders, not this schema.
  status: z.string().nullish(),
});

export const ApiOrderResponseSchema = z.object({
  id: z.number(),
  apiOrderUuid: z.uuid().nullable(),
  trackingUrl: z.string().nullish(),
  shipments: z.array(ApiOrderShipmentSchema).nullish(),
  schedulingUrl: zOrderDetailPayload.shape.scheduling_url,
  internalSchedulingUrl: zOrderDetailPayload.shape.internal_scheduling_url,
  additionalInformation: z.string().nullable(),
  prescriptionOrder: z.number().nullish(),
  parentOrder: z.number().nullable(),
  resupplyOrder: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastStatusChange: z.string(),
  isResupply: z.boolean().nullable(), // TODO Shouldn't be nullable
  confirmationStatus: z.enum(CONFIRMATION_STATUSES).nullish(),
  status: z.enum(ORDER_STATUSES),
  type: z.enum(TelehealthOptions).nullish(),
  targetShipDate: z.string().nullable(),
  prescriptionExpiration: z.string().nullable(),
  authorizationDate: z.string().nullable(),
  prescriptionMetadata: z
    .object({
      signingDate: z.string().nullable(),
      expirationDate: z.string().nullable(),
      source: z.string().nullable(),
      reference: z.string().nullable(),
    })
    .nullish(),
  items: z.array(ApiOrderItemSchema),
  patient: OrderPatientSchema,
  primaryContact: ApiOrderContactSchema,
  secondaryContact: ApiOrderContactSchema.nullable(),
  physician: ApiOrderPhysicianSchema.nullable(),
  insurance: InsuranceOrderResponseSchema,
  secondaryInsurance: InsuranceOrderResponseSchema.nullable(),
  otherInsurances: z.array(InsuranceOrderResponseSchema).nullish(),
  insuranceHistory: z
    .object({
      discovered: z.array(InsuranceOrderResponseSchema),
      updatedAt: z.string().nullish(),
    })
    .nullish(),
  shippingAddress: ApiOrderAddressSchema,
  supplier: z
    .object({
      supplierId: z.number(),
      name: z.string(),
      npi: z.string(),
      phone: z.string().nullable(),
      email: z.string().nullable(),
      brightreeNickname: z.string().nullable(),
      address: ApiOrderAddressSchema,
      requireRxDateGate: z.boolean().optional(),
    })
    .nullable(),
  user: z.object({
    createdAt: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    organizationId: z.number(),
    organizationName: z.string().nullable().optional(),
    phone: z.string().nullable(),
    role: z.string().nullable(),
    updatedAt: z.string().nullable(),
    userId: z.string(),
  }),
  assignees: z
    .array(
      z.object({
        firstName: z.string(),
        lastName: z.string(),
        userId: z.string(),
      })
    )
    .catch([]),
  orderStatus: z.array(
    // Is actually history
    z.object({
      status: z.string(), // for backward compatability, was z.nativeEnum(ORDER_STATUSES)
      reason: z.string().nullable(),
      description: z.string().nullable(),
      createdAt: z.string(),
    })
  ),
  telehealth: z
    .object({
      appointments: z.array(
        z.object({
          completedAt: z.string().nullable(),
          error: z.string().nullable(),
          scheduledFor: z.string().nullable(),
          updatedAt: z.string().nullable(),
          providerName: z.string().nullish(),
        })
      ),
    })
    .nullable(),
  telehealthPartnerId: z.coerce.number().nullable(),
  fulfillments: ApiOrderFulfillmentSchema.array(),
  esignatureId: z.string().nullable().optional(),
  esignature: z
    .object({
      esignatureId: z.string().nullable(),
      createdAt: z.string().nullable().optional(),
      updatedAt: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
      label: z.string().nullable().optional(),
      orderEsignatureStatus: z.array(
        z.object({
          createdAt: z.string(),
          status: z.string(),
        })
      ),
    })
    .nullable(),
  scriptStatus: zOrderResponse.shape.script_status.optional(),
  scriptSigningDate: zOrderResponse.shape.script_signing_date.optional(),
  scriptExpirationDate: zOrderResponse.shape.script_expiration_date.optional(),
  errors: z.array(z.any()).nullish(),
});

export function transformToOrder(data: z.infer<typeof ApiOrderResponseSchema>) {
  return {
    ...data,
    id: data.id.toString(),
    prescriptionOrder: data.prescriptionOrder ?? null,
    isResupply: data.isResupply ?? false,
    shipments: data.shipments ?? [],
    active: true, // TODO Fill in
    label: 'STUB_DATA', // deprecated
    esignatureId: data.esignature?.esignatureId ?? null,
    esignature: {
      id: data.esignature?.esignatureId ?? null,
      status: data.esignature?.status ?? null,
      label: data.esignature?.label ?? null,
      createdAt: data.esignature?.createdAt ?? null,
      updatedAt: data.esignature?.updatedAt ?? null,
    },
    prescriptionMetadata: data.prescriptionMetadata ?? null,
    items: data.items.map((item) => ({
      ...item,
      title: item.title ?? '',
      description: item.description ?? '',
      icds: item.icds.map((icd) => ({
        code: icd.code,
        description: icd.description ?? '',
      })),
      properties: (item.properties ?? []).map((property) => ({
        ...property,
        category: '',
        subcategory: null,
      })),
      savedProperties: item.selectedProperties.map((prop) => ({
        key: prop.key,
        validationType: null,
        value: prop.value ?? '',
      })),
      savedCheckoutProperties: item.selectedCheckoutProperties.map((prop) => ({
        key: prop.key,
        validationType: null,
        value: prop.value ?? '',
      })),
      categories: {
        // TODO Stop having it be this horrible shape
        // If there's 1 category, it's the categoryName
        // If there's 2, the first is the parent, second is the categoryName
        categoryName:
          item.categories.length > 1 ? (item.categories[1] ?? '') : (item.categories[0] ?? ''),
        ...(item.categories.length > 1 && {
          parentCategory: {
            categoryName: item.categories[0] ?? '',
          },
        }),
      },
      images: item.images ?? [],
      // TODO: Remove once backend DB is updated to match new variant.
      variants: (item.variants ?? []).map((variant) => {
        const { key, hcpc, attributes } = variant;
        // [{key: "age", value: "adult"}] -> {age: "adult"}
        const attributesObj: Record<string, string> = {};
        attributes.forEach((attr) => {
          attributesObj[attr.key] = attr.value;
        });
        const transformedVariant: ProductVariant = {
          key,
          productHcpcConfigurationUuid: variant.productHcpcConfigurationUuid,
          attributes: {
            hcpc: hcpc ?? [],
            ...attributesObj,
          } as { hcpc: string[] } & { [k: string]: string },
        };
        return transformedVariant;
      }),
    })),
    fulfillments: data.fulfillments.map((fulfillment) => ({
      ...fulfillment,
      orderId: fulfillment.orderId.toString(),
      active: fulfillment.isActive,
      productId: fulfillment.jsonData?.productDetails?.productId ?? null,
    })),
    patient: {
      ...data.patient,
      dob: dateOnly(data.patient.dob),
      email: data.patient.email ?? '',
      phoneNumber: data.patient.phoneNumber ?? '',
    },
    secondaryInsurance: data.secondaryInsurance ?? undefined,
    insuranceHistory: data.insuranceHistory ?? undefined,
    physician: data.physician
      ? {
          ...data.physician,
          email: data.physician.email ?? '',
          phone: data.physician.phone ?? '',
          patientLastVisit: data.physician.patientLastVisit ?? '',
        }
      : null,
    referral: {
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      phone: data.user.phone ?? '',
      email: data.user.email,
      organization: { name: data.user.organizationName ?? '' },
    },
    // TODO We need guaranteed supplier data from API
    supplier: {
      id: data.supplier ? data.supplier.supplierId.toString() : '',
      name: data.supplier ? data.supplier.name : 'Unknown Supplier',
      npi: data.supplier ? data.supplier.npi : '',
      phoneNumber: data.supplier?.phone ?? null,
      supportEmail: data.supplier?.email ?? null,
      addresses: data.supplier ? [data.supplier.address] : [],
      organization: { name: 'STUB_DATA' }, // TODO Fix, get name from API
      brightreeNickname: data.supplier?.brightreeNickname ?? null,
      requireRxDateGate: data.supplier?.requireRxDateGate ?? false,
    },
    assignee: data.assignees?.[0]
      ? {
          firstName: data.assignees[0].firstName,
          lastName: data.assignees[0].lastName,
          userId: data.assignees[0].userId,
        }
      : null,
    history: data.orderStatus.map((historyItem) => ({
      status: historyItem.status,
      reason: historyItem.reason ?? '',
      description: historyItem.description ?? '',
      createdAt: historyItem.createdAt,
    })),
    telehealth: data.telehealth
      ? {
          appointments: data.telehealth.appointments.map((appointment) => ({
            ...appointment,
            providerName: appointment.providerName ?? null,
          })),
        }
      : null,
  };
}

export type Order = ReturnType<typeof transformToOrder>;
export type OrderShipment = Order['shipments'][number];

export function isRefillable(order: Pick<Order, 'fulfillments' | 'items'>): boolean {
  return (
    order.fulfillments?.some((f) => (f.jsonData?.max_refills ?? 0) > 0) ||
    order.items?.some((item) => item.refillAmount > 0) ||
    false
  );
}

export type DisabledReason = { reason: string; action: string };

/** Eligibility shared by every resupply action, whole-order skip and per-item skip alike. */
export function getResupplyActionsDisabledReason(order: Order): DisabledReason | null {
  if (!isRefillable(order)) {
    return {
      reason: 'No items on this order have remaining refills.',
      action: 'Check the item refill amounts before trying to use resupply actions.',
    };
  }

  if (!order.isResupply) {
    return {
      reason: "This isn't a resupply order, so it can only be canceled.",
      action: 'Escalate if it should be treated as a resupply.',
    };
  }

  if (isClosedOrderStatus(order.status)) {
    return {
      reason: `This order is currently ${order.status}, so resupply changes are locked.`,
      action:
        'Change the order status to "Confirm Order" before skipping, if skipping is still appropriate.',
    };
  }

  return null;
}

export function getOrderSkipDisabledReason(order: Order): string | null {
  const resupplyActionsDisabledReason = getResupplyActionsDisabledReason(order);
  if (resupplyActionsDisabledReason) {
    return `${resupplyActionsDisabledReason.reason} ${resupplyActionsDisabledReason.action}`;
  }

  // Re-recording Skipped is refused with a 409, so block it here rather than opening a modal
  // whose submit cannot succeed. This also closes the window where a skip has been recorded but
  // the CANCELLED transition has not yet arrived over Kafka. A Confirmed or Changed order is
  // still skippable -- a patient can confirm over SMS and then call in to skip. Gates only the
  // whole-order skip: per-item skips go through the fulfillment activate/deactivate routes,
  // which do not touch confirmation_status.
  if (order.confirmationStatus === 'Skipped') {
    return 'This order has already been skipped. A replacement resupply order is being scheduled.';
  }

  // Once the child resupply exists, post_order_confirmation short-circuits on resupply_order, so
  // a skip would record the outcome and then do nothing -- no cancel, no new resupply.
  if (order.resupplyOrder) {
    return 'This order already has a resupply order scheduled, so it can no longer be skipped.';
  }

  return null;
}

export type DecoratedOrder = Order & {
  decorations: {
    actions: Array<OrderAction>;
    actionsButtonLabel: string;
    abilities: Array<ORDER_ABILITIES>;
    awaitingMyAcceptance: boolean;
    bgClassName: string;
    labelText: string;
    latestStatusChange: string | null;
    latestOnHoldItem: HistoryItem | null;
    lastUpdatedAt: string;
    quickAction?: OrderAction | null;
    sentToSupplierDate: string | null;
    isRefillable: boolean;
    isFutureOrder: boolean;
  };
};
export type DecoratedOrders = Array<DecoratedOrder>;

export enum OrderSortingEnum {
  ASCENDING_ORDER_DATE = 'ASCENDING_ORDER_DATE',
  ASCENDING_ORDER_ID = 'ASCENDING_ORDER_ID',
  DESCENDING_ORDER_DATE = 'DESCENDING_ORDER_DATE',
  DESCENDING_ORDER_ID = 'DESCENDING_ORDER_ID',
  ASCENDING_PATIENT_NAME = 'ASCENDING_PATIENT_NAME',
  DESCENDING_PATIENT_NAME = 'DESCENDING_PATIENT_NAME',
  ASCENDING_REFERRAL_NAME = 'ASCENDING_REFERRAL_NAME',
  DESCENDING_REFERRAL_NAME = 'DESCENDING_REFERRAL_NAME',
  ASCENDING_SUPPLIER_NAME = 'ASCENDING_SUPPLIER_NAME',
  DESCENDING_SUPPLIER_NAME = 'DESCENDING_SUPPLIER_NAME',
  ASCENDING_STATUS = 'ASCENDING_STATUS',
  DESCENDING_STATUS = 'DESCENDING_STATUS',
  ASCENDING_LAST_STATUS_CHANGE = 'ASCENDING_LAST_STATUS_CHANGE',
  DESCENDING_LAST_STATUS_CHANGE = 'DESCENDING_LAST_STATUS_CHANGE',
  ASCENDING_PROVIDER_NAME = 'ASCENDING_PROVIDER_NAME',
  DESCENDING_PROVIDER_NAME = 'DESCENDING_PROVIDER_NAME',
  ASCENDING_INSURANCE_STATE = 'ASCENDING_INSURANCE_STATE',
  DESCENDING_INSURANCE_STATE = 'DESCENDING_INSURANCE_STATE',
  ASCENDING_SCHEDULED_VISIT = 'ASCENDING_SCHEDULED_VISIT',
  DESCENDING_SCHEDULED_VISIT = 'DESCENDING_SCHEDULED_VISIT',
  ASCENDING_DOCUMENTATION = 'ASCENDING_DOCUMENTATION',
  DESCENDING_DOCUMENTATION = 'DESCENDING_DOCUMENTATION',
  ASCENDING_SCRIPT_STATUS = 'ASCENDING_SCRIPT_STATUS',
  DESCENDING_SCRIPT_STATUS = 'DESCENDING_SCRIPT_STATUS',
  ASCENDING_OLDEST_PENDING_AUTH = 'ASCENDING_OLDEST_PENDING_AUTH',
  DESCENDING_OLDEST_PENDING_AUTH = 'DESCENDING_OLDEST_PENDING_AUTH',
}

export type GetOrderTimelinePayload = {
  orderId: number;
};

export type TimelineData = {
  orderId: string;
  entries: {
    statusFrom: string;
    statusTo: string;
    text: string;
    createdAt: string;
  }[];
};

export type GetOrderTimelineResult = {
  getOrderTimeline: {
    timeline: TimelineData;
  };
};

export type OrderMessage = {
  createdAt: string;
  id: string;
  new: boolean;
  text: string;
  userFirstName?: string;
  userId: string;
  userLastName?: string;
  toConduit?: boolean;
};

type OrderNotificationType =
  | 'ORDER_CREATED'
  | 'ORDER_REJECTED'
  | 'NOTESFICATION_MESSAGE_CREATED'
  | 'CABINET_FILE_UPLOADED';

export enum OrderChannel {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  CONDUIT = 'CONDUIT',
}

export type OrderNotification = {
  channel: OrderChannel;
  content: string;
  contentVariables: Record<string, string>;
  createdAt: string;
  new: boolean;
  id: string;
  type: OrderNotificationType;
  url: string;
};

export enum Relationship {
  SELF = 'SELF',
  FAMILY_MEMBER = 'FAMILY_MEMBER',
  AIDE = 'AIDE',
  COORDINATOR = 'COORDINATOR',
  CASE_MANAGER = 'CASE_MANAGER',
  HEALTH_PROXY = 'HEALTH_PROXY',
  OTHER = 'OTHER',
}

export const RELATIONSHIP_OPTIONS = [
  { value: Relationship.SELF, label: 'Self' },
  { value: Relationship.FAMILY_MEMBER, label: 'Family Member' },
  { value: Relationship.AIDE, label: 'Aide' },
  { value: Relationship.COORDINATOR, label: 'Coordinator' },
  { value: Relationship.CASE_MANAGER, label: 'Case Manager' },
  { value: Relationship.HEALTH_PROXY, label: 'Health Proxy' },
  { value: Relationship.OTHER, label: 'Other' },
] as const;

export function humanizeRelationship(relationship: Relationship): string {
  const option = RELATIONSHIP_OPTIONS.find((opt) => opt.value === relationship);
  return option ? option.label : relationship;
}

export enum Language {
  ENGLISH = 'ENGLISH',
  SPANISH = 'SPANISH',
  RUSSIAN = 'RUSSIAN',
  CHINESE = 'CHINESE',
  ARABIC = 'ARABIC',
  OTHER = 'OTHER',
}

export const LANGUAGE_OPTIONS = [
  { value: Language.ENGLISH, label: 'English' },
  { value: Language.SPANISH, label: 'Spanish' },
  { value: Language.RUSSIAN, label: 'Russian' },
  { value: Language.CHINESE, label: 'Chinese' },
  { value: Language.ARABIC, label: 'Arabic' },
  { value: Language.OTHER, label: 'Other' },
] as const;

export function humanizeLanguage(language: Language): string {
  const option = LANGUAGE_OPTIONS.find((opt) => opt.value === language);
  return option ? option.label : language;
}

export enum PhoneType {
  MOBILE = 'MOBILE',
  LANDLINE = 'LANDLINE',
  OFFICE = 'OFFICE',
}

export const PHONE_TYPE_OPTIONS = [
  { value: PhoneType.MOBILE, label: 'Mobile' },
  { value: PhoneType.LANDLINE, label: 'Landline' },
  { value: PhoneType.OFFICE, label: 'Office' },
] as const;

export function humanizePhoneType(phoneType: PhoneType) {
  const option = PHONE_TYPE_OPTIONS.find((opt) => opt.value === phoneType);
  return option ? option.label : phoneType;
}

export enum GenderOptions {
  Male = 'MALE',
  Female = 'FEMALE',
  Other = 'OTHER',
}
export enum NewGenderOptions {
  Male = 'Male',
  Female = 'Female',
  Other = 'Other',
}

// Deprecated
export const genderSelectOptions: Array<{ value: string; label: string }> = [
  { label: 'Male', value: GenderOptions.Male },
  { label: 'Female', value: GenderOptions.Female },
  { label: 'Other', value: GenderOptions.Other },
];

export const GENDER_OPTIONS = [
  { value: GenderOptions.Male, label: 'Male' },
  { value: GenderOptions.Female, label: 'Female' },
  { value: GenderOptions.Other, label: 'Other' },
] as const;

export function identifyGender(str: string): GenderOptions {
  return Object.values(GenderOptions).includes(str.toUpperCase() as GenderOptions)
    ? (str.toUpperCase() as GenderOptions)
    : GenderOptions.Male;
}

export function humanizeGender(gender: GenderOptions) {
  const option = GENDER_OPTIONS.find((opt) => opt.value === gender);
  return option ? option.label : gender;
}

export const STATE_OPTIONS: Array<{ value: string; label: string }> = US_STATES.map((state) => ({
  label: state,
  value: state,
}));
